import { useState } from "react";
import { api, type Flag, type HeldShare } from "./api";
import { GalleryPlayer } from "./GalleryPlayer";
import "./ClassShare.css";

// The teacher's choice on a share screening held back (decisions 61, 62), inside the "Flagged for
// you" list on Home: play it (or see the value), then show it to the class or keep it hidden.
// Classmates see nothing until the teacher chooses "Show to class".

/** Fired after a choice, so the class gallery on the same page shows a newly approved project. */
export const GALLERY_CHANGED = "buildlab:gallery-changed";

export function ShareReview({ flag, onDone }: { flag: Flag; onDone: () => void }) {
  const [held, setHeld] = useState<HeldShare | null>(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const look = async () => {
    setError(null);
    try {
      const h = held ?? (await api.classShare(flag.id));
      setHeld(h);
      if (h.kind === "gallery") setPlaying(true);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const decide = async (show: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await (show ? api.approveClassShare(flag.id) : api.hideClassShare(flag.id));
      setPlaying(false);
      onDone();
      window.dispatchEvent(new Event(GALLERY_CHANGED)); // the class gallery on Home reloads
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const choices = (
    <>
      <button type="button" className="primary small" disabled={busy} onClick={() => decide(true)}>
        Show to class
      </button>
      <button type="button" className="outline small cs-hide" disabled={busy} onClick={() => decide(false)}>
        Keep hidden
      </button>
    </>
  );

  return (
    <div className="cs-review">
      <div className="row">
        <button type="button" className="outline small" onClick={look}>
          {flag.shareKind === "gallery" ? "Play it" : "See the value"}
        </button>
        {choices}
      </div>
      {held?.kind === "class_data" && (
        <pre className="cs-value">{held.data ? `${held.data.key}: ${JSON.stringify(held.data.value, null, 2)}` : "This value isn't waiting any more."}</pre>
      )}
      {error && <p className="page-error small">{error}</p>}
      {playing && held?.kind === "gallery" && (
        <GalleryPlayer files={held.entry.files} title={held.entry.title} byline={held.entry.byline} onClose={() => setPlaying(false)}>
          {choices}
        </GalleryPlayer>
      )}
    </div>
  );
}
